import { INSTAGRAM_CAROUSEL_MAX_ITEMS, INSTAGRAM_CAROUSEL_MIN_ITEMS } from '@mondaygpt/constants'
import { useCallback, useMemo } from 'react'
import { FieldGroup } from '../components/FieldGroup'
import { fieldLengthState, platformContentViolations } from '../utils/validatePlatformContent'
import type { PickerImageSelection } from '../components/ImagePickerModal'
import { useEditorHighlight } from '../hooks/useEditorHighlight'
import { useFlushPersistNow, useUpdateCreativeContent } from '../store/draft-creative-context'
import type { Creative, CreativeType, InstagramMediaItem, InstagramPostContent } from '../types'
import { MAX_HASHTAGS } from '../types'
import { validateInstagramCarousel } from '../utils/validateInstagramCarousel'
import { HashtagInput } from './fields/HashtagInput'
import { ImagePickerBody } from './fields/ImagePickerBody'
import { MediaItemStrip } from './fields/MediaItemStrip'
import { VideoPickerBody } from './fields/VideoPickerBody'

interface InstagramPostEditorProps {
  creative: Creative<CreativeType.InstagramPost>
}

export function InstagramPostEditor({ creative }: InstagramPostEditorProps) {
  const updateContent = useUpdateCreativeContent()
  const flushPersistNow = useFlushPersistNow()
  const { editorRef, highlightedSection, handleEditorBlur, setActiveSection } = useEditorHighlight(
    creative.id
  )
  const content = creative.content

  // Instagram's published caption limit: 2,200 characters, 30 hashtags, 20 @ mentions
  // (Meta IG User /media reference). The counter measures the caption PLUS the
  // first five hashtags — that concatenation is what InstagramPublishService
  // actually sends, so a caption just under the cap can still be pushed over.
  const violationByField = useMemo(() => {
    const map = new Map<string, string>()
    for (const v of platformContentViolations({ type: creative.type, content })) {
      if (!map.has(v.contentField)) map.set(v.contentField, v.message)
    }
    return map
  }, [creative.type, content])
  const captionLength = fieldLengthState(creative.type, 'caption', content)
  const mediaItems = content.mediaItems ?? []
  // Editor enters carousel mode at the first added item so the strip + "add one more"
  // hint render. Publishability still requires INSTAGRAM_CAROUSEL_MIN_ITEMS.
  const inCarouselMode = mediaItems.length >= 1
  const hasVideo = !!(content.videoAttachmentId || content.videoUrl || content.videoStatus)

  const update = useCallback(
    (field: keyof InstagramPostContent, value: unknown) => {
      updateContent(creative.id, { [field]: value })
    },
    [creative.id, updateContent]
  )

  const carouselValidation = useMemo(
    () =>
      mediaItems.length >= INSTAGRAM_CAROUSEL_MIN_ITEMS
        ? validateInstagramCarousel(mediaItems)
        : { ok: true as const },
    [mediaItems]
  )

  const persistMediaItems = useCallback(
    (next: InstagramMediaItem[]) => {
      const first = next[0]
      updateContent(creative.id, {
        mediaItems: next,
        // mirror the first item to legacy fields so older readers stay coherent
        imageUrl: first?.url ?? null,
        imageAttachmentId: first?.attachmentId ?? null,
      })
      // Carousel edits are discrete picker actions — flush so a quick refresh
      // doesn't drop the queued PUT.
      flushPersistNow(creative.id)
    },
    [creative.id, updateContent, flushPersistNow]
  )

  const handleAppendOne = useCallback(
    (selection: PickerImageSelection) => {
      // Preserve ratio/mimeType from the picker so the carousel preflight
      // validator can run client-side. Without these, the validator falls
      // back to a "Re-upload" message and Publish stays disabled.
      const addition: InstagramMediaItem = {
        attachmentId: selection.imageAttachmentId,
        url: selection.imageUrl,
        type: 'image',
        ratio: selection.ratio,
        mimeType: selection.mimeType,
      }
      persistMediaItems([...mediaItems, addition].slice(0, INSTAGRAM_CAROUSEL_MAX_ITEMS))
    },
    [mediaItems, persistMediaItems]
  )

  const handleSelectSingleImage = useCallback(
    (selection: PickerImageSelection) => {
      updateContent(creative.id, {
        imageUrl: selection.imageUrl,
        imageAttachmentId: selection.imageAttachmentId,
      })
      flushPersistNow(creative.id)
    },
    [creative.id, updateContent, flushPersistNow]
  )

  const selectedImagesForPicker = useMemo(
    () =>
      mediaItems.map(item => ({
        attachmentId: item.attachmentId,
        imageUrl: item.url,
        mimeType: item.mimeType,
        ratio: item.ratio,
      })),
    [mediaItems]
  )
  const remainingSlots = Math.max(0, INSTAGRAM_CAROUSEL_MAX_ITEMS - mediaItems.length)

  return (
    <div ref={editorRef} className="space-y-4" onBlurCapture={handleEditorBlur}>
      <FieldGroup
        label={
          inCarouselMode ? `Images (${mediaItems.length}/${INSTAGRAM_CAROUSEL_MAX_ITEMS})` : 'Image'
        }
        sectionId="image"
        highlighted={highlightedSection === 'image'}
      >
        {inCarouselMode ? (
          <div className="space-y-3">
            <MediaItemStrip
              items={mediaItems}
              maxItems={INSTAGRAM_CAROUSEL_MAX_ITEMS}
              onChange={persistMediaItems}
              creativeId={creative.id}
            />
            {!carouselValidation.ok && (
              <p className="text-xs text-[color:var(--alert-error-text)]">
                {carouselValidation.message}
              </p>
            )}
            {mediaItems.length === 1 && (
              <p className="text-xs text-paper-dim">
                Add at least one more image to publish as a carousel.
              </p>
            )}
            <ImagePickerBody
              creativeId={creative.id}
              onSelect={handleAppendOne}
              selectedImages={selectedImagesForPicker}
              disabled={remainingSlots === 0}
              disabledHint={`Carousel full (${INSTAGRAM_CAROUSEL_MAX_ITEMS}/${INSTAGRAM_CAROUSEL_MAX_ITEMS})`}
              acceptMime="image/jpeg"
            />
          </div>
        ) : (
          <div className="space-y-3">
            {content.imageUrl && (
              <img
                src={content.imageUrl}
                alt=""
                className="h-40 w-full rounded-[10px] border border-line bg-bg-3 object-cover"
              />
            )}
            <ImagePickerBody
              creativeId={creative.id}
              currentImageUrl={content.imageUrl}
              currentAttachmentId={content.imageAttachmentId}
              onSelect={handleSelectSingleImage}
            />
          </div>
        )}
      </FieldGroup>

      {hasVideo && !inCarouselMode && (
        <FieldGroup label="Video" sectionId="video" highlighted={highlightedSection === 'video'}>
          <VideoPickerBody
            creativeId={creative.id}
            creativeType={creative.type}
            platform="instagram"
            selectedVideoAttachmentId={content.videoAttachmentId}
            selectedVideoUrl={content.videoUrl}
            onSelectVideo={(attachmentId, videoUrl, videoSource) => {
              updateContent(creative.id, { videoAttachmentId: attachmentId, videoUrl, videoSource })
              flushPersistNow(creative.id)
            }}
          />
        </FieldGroup>
      )}

      <FieldGroup
        label="Caption"
        count={captionLength ? `${captionLength.count}/${captionLength.max}` : undefined}
        countOver={captionLength?.over}
        error={violationByField.get('caption')}
        sectionId="caption"
        highlighted={highlightedSection === 'caption'}
      >
        <textarea
          value={content.caption}
          onChange={e => update('caption', e.target.value)}
          onFocusCapture={() => setActiveSection(creative.id, 'caption')}
          placeholder="Write your caption..."
          rows={4}
          className="w-full resize-none rounded-[10px] border border-line-bright bg-bg-3 px-3 py-2 text-sm text-paper outline-none placeholder:text-paper-faint"
        />
      </FieldGroup>

      <FieldGroup
        label={`Hashtags (${content.hashtags.length}/${MAX_HASHTAGS})`}
        count={content.hashtags.length >= MAX_HASHTAGS ? 'Limit reached' : undefined}
        sectionId="hashtags"
        highlighted={highlightedSection === 'hashtags'}
      >
        <HashtagInput
          hashtags={content.hashtags}
          onChange={hashtags => update('hashtags', hashtags)}
          maxHashtags={MAX_HASHTAGS}
          onFocusCapture={() => setActiveSection(creative.id, 'hashtags')}
        />
      </FieldGroup>
    </div>
  )
}
